/**
 * InvoicesPanel.jsx - Finance Invoice > Invoices: every invoice in the
 * ledger with what has been paid and what is left. Search, filter by status,
 * customer, project and invoice date, export the list as CSV, open an
 * invoice to edit it, record a payment or void it, and add a new one.
 * A row's project opens on Projects (its existing deep link).
 */
import { useMemo, useState } from 'react';
import { exportCsv } from '../../utils/exportCsv';
import { fmtDate } from '../../utils/format';
import { Msg, num, td, th } from '../spareParts/spUi';
import { Btn, GREEN, LINE, MUTED, SelectInput, TextInput, fmtMoney } from '../quotes/quoteUi';
import { StatusChip } from './InvoiceModal';
import { STATUSES, csvRows, customersOf, filterInvoices, numberLabel, projectHref, projectLabel, totalsOf } from './invoiceRules';

const Stat = ({ label, value, tone }) => (
  <div style={{ background: '#fff', border: `1px solid ${LINE}`, borderRadius: 10, padding: '10px 14px', minWidth: 150 }}>
    <div style={{ fontSize: 9.5, fontWeight: 800, color: MUTED, letterSpacing: 1, textTransform: 'uppercase' }}>{label}</div>
    <div style={{ fontSize: 18, fontWeight: 800, color: tone || '#0D0D0D', fontVariantNumeric: 'tabular-nums', marginTop: 2 }}>{value}</div>
  </div>
);

export default function InvoicesPanel({
  data,
  error,
  editable,
  projects,
  project,
  onProject,
  customer = '',
  onCustomer,
  includeVoid,
  onIncludeVoid,
  onOpen,
  onNew,
}) {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const all = useMemo(() => (data && data.invoices) || [], [data]);
  const rows = useMemo(
    () => filterInvoices(all, { q, status, customer, project, from, to }),
    [all, q, status, customer, project, from, to]
  );
  const totals = totalsOf(rows);
  // rows still to review (carried over from before the ledger) are not owed money yet - as in Receivables
  const owed = totalsOf(rows.filter((r) => r.status !== 'Needs review'));
  const overdue = rows.filter((r) => r.status === 'Overdue');
  const review = all.filter((r) => r.status === 'Needs review');
  const projectRow = project ? projects.find((p) => String(p.id) === String(project)) : null;

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <Stat label="Invoiced" value={fmtMoney(totals.amount)} />
        <Stat label="Collected" value={fmtMoney(totals.paid)} tone={GREEN} />
        <Stat label="Open balance" value={fmtMoney(owed.balance)} tone="#92400E" />
        <Stat
          label="Overdue"
          value={`${overdue.length} · ${fmtMoney(overdue.reduce((s, r) => s + (Number(r.balance) || 0), 0))}`}
          tone={overdue.length ? '#B71C1C' : undefined}
        />
      </div>
      {review.length > 0 && status !== 'Needs review' && (
        <div
          style={{
            padding: '8px 12px',
            background: '#FFF3E0',
            border: '1px solid #FFE0B2',
            borderRadius: 8,
            fontSize: 11.5,
            color: '#7A3E00',
            marginBottom: 10,
          }}
        >
          {review.length} {review.length === 1 ? 'invoice needs' : 'invoices need'} review - carried over from before the ledger, waiting
          for an amount or a check. They stay out of receivables and aging until reviewed.{' '}
          <button
            type="button"
            onClick={() => setStatus('Needs review')}
            style={{ all: 'unset', cursor: 'pointer', fontWeight: 700, textDecoration: 'underline' }}
          >
            Show them
          </button>
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
        <TextInput
          aria-label="Search invoices"
          value={q}
          onChange={setQ}
          placeholder="Search invoice #, customer, project, quote, PO..."
          style={{ maxWidth: 320 }}
        />
        <SelectInput
          aria-label="Status"
          value={status}
          onChange={setStatus}
          blank="All statuses"
          options={[{ value: 'unpaid', label: 'Unpaid (open, partial, overdue)' }, ...STATUSES.filter((s) => s !== 'Void' || includeVoid)]}
          style={{ width: 200 }}
        />
        <SelectInput
          aria-label="Customer"
          value={customer}
          onChange={onCustomer}
          blank="All customers"
          options={customersOf(all)}
          style={{ width: 190 }}
        />
        <TextInput aria-label="Invoice date from" type="date" value={from} onChange={setFrom} style={{ width: 140 }} />
        <span style={{ fontSize: 11, color: MUTED }}>to</span>
        <TextInput aria-label="Invoice date to" type="date" value={to} onChange={setTo} style={{ width: 140 }} />
        <label style={{ display: 'inline-flex', gap: 5, alignItems: 'center', fontSize: 11.5, fontWeight: 600 }}>
          <input type="checkbox" checked={includeVoid} onChange={(e) => onIncludeVoid(e.target.checked)} /> Show void
        </label>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <Btn
            small
            onClick={() => exportCsv(`invoices-${new Date().toLocaleDateString('en-CA')}.csv`, csvRows(rows))}
            disabled={!rows.length}
          >
            Export CSV
          </Btn>
          {editable && (
            <Btn primary small onClick={onNew}>
              + New invoice
            </Btn>
          )}
        </div>
      </div>
      {projectRow && (
        <div style={{ fontSize: 11.5, marginBottom: 8 }}>
          Showing {projectLabel({ ...projectRow, project_name: projectRow.name })}
          {projectRow.customer ? ` (${projectRow.customer})` : ''} only.{' '}
          <button
            type="button"
            onClick={() => onProject(null)}
            style={{ all: 'unset', cursor: 'pointer', fontWeight: 700, color: GREEN, textDecoration: 'underline' }}
          >
            Show every project
          </button>
        </div>
      )}
      <Msg msg={error ? { kind: 'error', text: error } : null} />
      {!data && !error && <div style={{ padding: 30, color: MUTED, fontSize: 12 }}>Loading the invoices...</div>}
      {data && (
        <div style={{ border: `1px solid ${LINE}`, borderRadius: 10, overflow: 'auto', background: '#fff' }}>
          <table aria-label="Invoices" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Invoice #</th>
                <th style={th}>Date</th>
                <th style={th}>Due</th>
                <th style={th}>Customer</th>
                <th style={th}>Project</th>
                <th style={{ ...th, textAlign: 'right' }}>Amount</th>
                <th style={{ ...th, textAlign: 'right' }}>Paid</th>
                <th style={{ ...th, textAlign: 'right' }}>Balance</th>
                <th style={th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  tabIndex={0}
                  className="go-focus"
                  onClick={() => onOpen(r.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') onOpen(r.id);
                  }}
                  title={r.needs_review ? r.review_note || 'Needs review' : `Open invoice ${numberLabel(r)}`}
                  style={{ cursor: 'pointer', opacity: r.status === 'Void' ? 0.55 : 1 }}
                >
                  <td style={{ ...td, fontFamily: 'JetBrains Mono, Consolas, monospace', fontWeight: 700, color: GREEN }}>
                    {numberLabel(r)}
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{r.invoice_date ? fmtDate(r.invoice_date) : '-'}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap', color: r.status === 'Overdue' ? '#B71C1C' : undefined }}>
                    {r.effective_due ? fmtDate(r.effective_due) : '-'}
                  </td>
                  <td style={td}>{r.customer || '-'}</td>
                  <td style={td}>
                    <a href={projectHref(r)} onClick={(e) => e.stopPropagation()} style={{ color: '#1F2328', fontWeight: 600 }}>
                      {projectLabel(r)}
                    </a>
                    {r.po_number ? <div style={{ fontSize: 10, color: MUTED }}>PO {r.po_number}</div> : null}
                  </td>
                  <td style={num}>{r.amount === null ? <span style={{ color: '#A04A00' }}>to enter</span> : fmtMoney(r.amount)}</td>
                  <td style={num}>{r.paid ? fmtMoney(r.paid) : '-'}</td>
                  <td style={{ ...num, fontWeight: 700 }}>{r.balance === null ? '-' : fmtMoney(r.balance)}</td>
                  <td style={td}>
                    <StatusChip status={r.status} />
                    {r.status === 'Overdue' && <span style={{ fontSize: 10, color: '#B71C1C', marginLeft: 6 }}>{r.days_past_due}d</span>}
                  </td>
                </tr>
              ))}
              {!rows.length && (
                <tr>
                  <td colSpan={9} style={{ ...td, textAlign: 'center', color: MUTED, padding: 24 }}>
                    No invoices match.
                  </td>
                </tr>
              )}
            </tbody>
            {rows.length > 0 && (
              <tfoot>
                <tr style={{ background: '#F6F9F3', fontWeight: 800 }}>
                  <td style={td} colSpan={5}>
                    Total · {totals.count} {totals.count === 1 ? 'invoice' : 'invoices'}
                  </td>
                  <td style={num}>{fmtMoney(totals.amount)}</td>
                  <td style={num}>{fmtMoney(totals.paid)}</td>
                  <td style={num}>{fmtMoney(totals.balance)}</td>
                  <td style={td} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
}
